import type { JSX } from "react";
import { useState } from "react";

import useAppMonitor from "@/services/AppMonitor";
import WebViewModal from "@/ui/composed/media/webView/WebViewModal";
import { Text } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { openBrowserTab, openUrl } from "@/utils/navigation.util";
import { isWeb } from "@/utils/platform.util";

type LinkType = "sms" | "tel" | "web";

interface LinkPressProps {
  field: string;
  linkType: LinkType;
  text: string;
}

const Bullet = (
  <Box paddingHorizontal={10} paddingVertical={9}>
    <Box
      backgroundColor={ColorToken.NeutralPrimary}
      borderRadius={3}
      height={3}
      width={3}
    />
  </Box>
);

interface TextProps extends ViewWithTestID {
  isFlexShrink?: boolean;
  text: string;
}

const BulletPointText = ({ isFlexShrink = true, testID, text }: TextProps) => {
  return (
    <Box flexShrink={isFlexShrink ? 1 : 0}>
      <Text testID={testID} variant="body">
        {text}
      </Text>
    </Box>
  );
};

interface LinkTextProps extends TextProps {
  linkType: LinkType;
  onPress?: () => void;
}

const LinkText = ({
  isFlexShrink = true,
  linkType,
  onPress,
  testID,
  text,
}: LinkTextProps) => {
  const { captureException } = useAppMonitor();
  const [isWebviewOpen, setIsWebviewOpen] = useState<boolean>(false);
  const [isPressed, setIsPressed] = useState<boolean>(false);

  const handlePress = () => {
    onPress?.();
    if (linkType === "web") {
      if (isWeb()) {
        openBrowserTab(text);
        return;
      }
      setIsWebviewOpen(true);
      return;
    }

    // only remaining linkTypes are "sms", and "tel", which are needed as scheme here
    void openUrl({
      onError: (error?: Error) => {
        captureException(
          new Error(`Couldn't open '${linkType}' link with value ${text}`, {
            cause: error,
          }),
        );
      },
      scheme: linkType,
      url: text,
    });
  };

  return (
    <>
      <Box
        flexShrink={isFlexShrink ? 1 : 0}
        onPress={handlePress}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
      >
        <Text
          color={ColorToken.Primary100}
          testID={testID}
          textDecorationLine={isPressed ? "underline" : "none"}
          variant="bodyMedium"
        >
          {text}
        </Text>
      </Box>
      {!isWeb() && (
        <WebViewModal
          isOpen={isWebviewOpen}
          onClose={() => setIsWebviewOpen(false)}
          title={text}
          uri={text}
        />
      )}
    </>
  );
};

const SafetyPlanSection = ({
  shouldPrefixWithBullet,
  statements,
  testID,
  title,
}: {
  shouldPrefixWithBullet?: boolean;
  statements?: JSX.Element[] | string[];
  testID: string;
  title: string;
}) => {
  return (
    <VStack gap={6}>
      <Text
        color={ColorToken.NeutralSecondary}
        testID={testID}
        variant="caption"
      >
        {title}
      </Text>
      <VStack gap={8}>
        {/* eslint-disable-next-line max-params */}
        {statements?.map((statement, index) => {
          const isEmpty =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, @typescript-eslint/no-base-to-string -- currently reported in SonarQube as unresolved
            statement && statement.toString().includes("undefined");
          if (isEmpty) return null;
          const key = `${testID}_statement-${index}`;
          return (
            <HStack alignItems="flex-start" key={key}>
              {shouldPrefixWithBullet && Bullet}
              {typeof statement === "string" ? (
                <BulletPointText testID={key} text={statement} />
              ) : (
                <Box flexShrink={1}>{statement}</Box>
              )}
            </HStack>
          );
        })}
      </VStack>
    </VStack>
  );
};

export { BulletPointText, LinkText };
export type { LinkPressProps, LinkType };
export default SafetyPlanSection;
